import { entrarConSesionCompartida, borrarSesion } from '../ui/sesion.js';
import Entrando from '../ui/Entrando.jsx';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
// Sistema de diseño de la suite: tokens de color y piezas comunes, para que esta
// pantalla se lea igual que el resto del CRM.
import { C } from '../ui/theme.js'
import { Pill, EmptyState, Th, Td, TableWrap } from '../ui/ui.jsx'
// Alias porque este fichero ya tiene su propio `StatCard`: ese envoltorio se
// queda con las mismas props y por dentro pinta la tarjeta del sistema.
import { StatCard as StatCardSistema, Ic } from '../ui/premium.jsx'

// ============================================================================
// CONFIGURACIÓN SUPABASE - DIMOAUTO (LOGIN)
// ============================================================================
const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY'

// Cliente Supabase simple para datos
const supabase = {
  from: (table) => ({
    select: async (columns = '*') => {
      const url = `${SUPABASE_URL}/rest/v1/${table}?select=${columns}`
      try {
        const response = await fetch(url, {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        })
        if (!response.ok) throw new Error(`Error ${response.status}`)
        const data = await response.json()
        return { data, error: null }
      } catch (error) {
        return { data: null, error }
      }
    }
  })
}

// ============================================================================
// SUPABASE AUTH (Email/Password)
// ============================================================================
const supabaseAuth = {
  signIn: async (email, password) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })
      if (!response.ok) {
        const error = await response.json()
        return { data: null, error: error.error_description || 'Credenciales incorrectas' }
      }
      const data = await response.json()
      localStorage.setItem('sb-access-token', data.access_token)
      localStorage.setItem('sb-refresh-token', data.refresh_token)
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },
  signOut: async () => {
    const token = localStorage.getItem('sb-access-token')
    if (token) {
      try {
        await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
          method: 'POST',
          headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}` }
        })
      } catch (e) { /* ignore */ }
    }
    localStorage.removeItem('sb-access-token')
    localStorage.removeItem('sb-refresh-token')
  },
  getCurrentUser: async () => {
    const token = localStorage.getItem('sb-access-token')
    if (!token) return null
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) return null
      return await response.json()
    } catch { return null }
  }
}

// Buscar admin por auth_user_id
const findAdminByAuthId = async (authUserId) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/administradores?auth_user_id=eq.${authUserId}&activo=eq.true&select=*`
    const response = await fetch(url, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` }
    })
    const data = await response.json()
    return data && data.length > 0 ? data[0] : null
  } catch { return null }
}

// ============================================================================
// CONFIGURACIÓN SUPABASE - CUSTOMER 360 (DATOS)
// ============================================================================
const CUSTOMER_SUPABASE_URL = 'https://ohjlkorrqwjpncgtnpkc.supabase.co'
const CUSTOMER_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9oamxrb3JycXdqcG5jZ3RucGtjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njg5ODk3ODQsImV4cCI6MjA4NDU2NTc4NH0.UXnFID9ehQms1Uoj-ExBM8j0j-wF6up5kYcUEdszL6s'

// Cliente para datos de Customer 360
const customerSupabase = {
  from: (table) => ({
    select: async (columns = '*') => {
      const url = `${CUSTOMER_SUPABASE_URL}/rest/v1/${table}?select=${columns}`
      try {
        const response = await fetch(url, {
          headers: {
            'apikey': CUSTOMER_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${CUSTOMER_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        })
        if (!response.ok) throw new Error(`Error ${response.status}`)
        const data = await response.json()
        return { data, error: null }
      } catch (error) {
        return { data: null, error }
      }
    }
  })
}

// ============================================================================
// CONFIGURACIÓN BACKEND RENDER.COM (Para logs en vivo)
// ============================================================================
const BACKEND_URL = 'https://c360-backend.dimoauto.com' // Backend c360-backendvision en Coolify Hetzner — subdominio propio con cert LE

// ============================================================================
// MODO DEMO - DESACTIVADO PARA DATOS REALES
// ============================================================================
const DEMO_MODE = false // ← Conecta con datos reales

const DEMO_MARCAS = [
  { id: '1', nombre: 'Peugeot', codigo: 'PEU', activa: true, sesion_valida: true, leads_24h: 8, leads_7d: 42, tiempo_respuesta_promedio: 12 },
  { id: '2', nombre: 'Citroën', codigo: 'CIT', activa: true, sesion_valida: true, leads_24h: 5, leads_7d: 31, tiempo_respuesta_promedio: 15 },
  { id: '3', nombre: 'Opel', codigo: 'OPE', activa: true, sesion_valida: false, leads_24h: 3, leads_7d: 18, tiempo_respuesta_promedio: 22 },
  { id: '4', nombre: 'Fiat', codigo: 'FIA', activa: true, sesion_valida: true, leads_24h: 6, leads_7d: 28, tiempo_respuesta_promedio: 11 },
  { id: '5', nombre: 'Fiat Pro', codigo: 'FIAPRO', activa: true, sesion_valida: true, leads_24h: 2, leads_7d: 10, tiempo_respuesta_promedio: 13 },
  { id: '6', nombre: 'Jeep', codigo: 'JEE', activa: true, sesion_valida: true, leads_24h: 2, leads_7d: 12, tiempo_respuesta_promedio: 18 },
  { id: '7', nombre: 'Abarth', codigo: 'ABA', activa: true, sesion_valida: true, leads_24h: 1, leads_7d: 5, tiempo_respuesta_promedio: 9 },
  { id: '8', nombre: 'Leapmotor', codigo: 'LEA', activa: false, sesion_valida: false, leads_24h: 0, leads_7d: 0, tiempo_respuesta_promedio: null },
]

const DEMO_LEADS = [
  { id: '1', nombre_cliente: 'Omar Murillo', telefono: '+34919010044', marca_nombre: 'Citroën', producto_interes: 'CITROËN JUMPER', tipo_cliente: 'B2B', estado: 'procesado', tiempo_respuesta_mins: 8, created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString() },
  { id: '2', nombre_cliente: 'Ricardo Pérez García', telefono: '+34612345678', marca_nombre: 'Peugeot', producto_interes: 'PEUGEOT 3008 GT', tipo_cliente: 'B2C', estado: 'procesado', tiempo_respuesta_mins: 12, created_at: new Date(Date.now() - 1000 * 60 * 60).toISOString() },
  { id: '3', nombre_cliente: 'María García López', telefono: '+34698765432', marca_nombre: 'Fiat', producto_interes: 'FIAT 500e', tipo_cliente: 'B2C', estado: 'procesado', tiempo_respuesta_mins: 5, created_at: new Date(Date.now() - 1000 * 60 * 90).toISOString() },
  { id: '4', nombre_cliente: 'José Antonio López', telefono: '+34611223344', marca_nombre: 'Opel', producto_interes: 'OPEL CORSA Electric', tipo_cliente: 'B2C', estado: 'error', tiempo_respuesta_mins: null, created_at: new Date(Date.now() - 1000 * 60 * 120).toISOString(), error_mensaje: 'Timeout al confirmar lead' },
  { id: '5', nombre_cliente: 'Ana Martínez Ruiz', telefono: '+34677889900', marca_nombre: 'Jeep', producto_interes: 'JEEP AVENGER', tipo_cliente: 'B2B', estado: 'procesado', tiempo_respuesta_mins: 18, created_at: new Date(Date.now() - 1000 * 60 * 180).toISOString() },
  { id: '6', nombre_cliente: 'Carlos Ruiz Fernández', telefono: '+34655443322', marca_nombre: 'Peugeot', producto_interes: 'PEUGEOT 208', tipo_cliente: 'B2C', estado: 'procesado', tiempo_respuesta_mins: 9, created_at: new Date(Date.now() - 1000 * 60 * 240).toISOString() },
  { id: '7', nombre_cliente: 'Laura Sánchez Díaz', telefono: '+34699887766', marca_nombre: 'Abarth', producto_interes: 'ABARTH 595', tipo_cliente: 'B2C', estado: 'procesado', tiempo_respuesta_mins: 14, created_at: new Date(Date.now() - 1000 * 60 * 300).toISOString() },
  { id: '8', nombre_cliente: 'Miguel Fernández Gil', telefono: '+34622334455', marca_nombre: 'Fiat', producto_interes: 'FIAT TIPO Cross', tipo_cliente: 'B2B', estado: 'procesado', tiempo_respuesta_mins: 21, created_at: new Date(Date.now() - 1000 * 60 * 360).toISOString() },
]

const DEMO_ULTIMA_EJECUCION = {
  ejecutado_en: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
  estado: 'completado',
  marcas_procesadas: 6,
  leads_procesados: 8,
  duracion_segundos: 127
}

// ============================================================================
// COLORES Y LOGOS POR MARCA
// ============================================================================
const MARCA_CONFIG = {
  'Peugeot': { bg: '#1a2b4a', text: '#fff', logo: '/marcas/peugeot.png' },
  'Citroën': { bg: '#ac1e2d', text: '#fff', logo: '/marcas/citroen.png' },
  'Opel': { bg: '#f5c518', text: '#000', logo: '/marcas/opel.png' },
  'Fiat': { bg: '#8b0023', text: '#fff', logo: '/marcas/fiat.png' },
  'Fiat Pro': { bg: '#8b0023', text: '#fff', logo: '/marcas/fiat.png', isPro: true },
  'Jeep': { bg: '#4a5f31', text: '#fff', logo: '/marcas/jeep.png' },
  'Abarth': { bg: '#1f2a37', text: '#fff', logo: '/marcas/abarth.png' },
  'Leapmotor': { bg: '#00b4d8', text: '#fff', logo: '/marcas/leapmotor.png' },
}

// ============================================================================
// UTILIDADES
// ============================================================================
function formatTimeAgo(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffMins < 1) return 'Ahora'
  if (diffMins < 60) return `${diffMins} min`
  if (diffHours < 24) return `${diffHours}h`
  return `${diffDays}d`
}

// Igualado al formato usado en EmailLeadsApp ("dd/mm/aaaa, hh:mm") para que
// la columna "Fecha" de la tabla Leads Recientes muestre fecha absoluta
// en lugar de relativa "1h"/"5d" — alineado con el diseño de Email Leads.
function formatDateTime(dateString) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ============================================================================
// ANIMATED LOGIN BACKGROUND
// ============================================================================
const AnimatedLoginBackground = forwardRef(({ cardRect }, ref) => {
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const stateRef = useRef({ nodes: [], clickBoost: 0 })
  
  useImperativeHandle(ref, () => ({
    triggerPulse: () => { stateRef.current.clickBoost = 1 }
  }))
  
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let width, height
    const numNodes = 80, connectionDistance = 180, nodeSpeed = 0.3
    
    const createNodes = () => {
      const nodes = []
      for (let i = 0; i < numNodes; i++) {
        nodes.push({
          x: Math.random() * width, y: Math.random() * height,
          vx: (Math.random() - 0.5) * nodeSpeed, vy: (Math.random() - 0.5) * nodeSpeed,
          radius: 2 + Math.random() * 2, baseRadius: 2 + Math.random() * 2
        })
      }
      return nodes
    }
    
    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = window.innerWidth
      height = window.innerHeight
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      if (stateRef.current.nodes.length === 0) stateRef.current.nodes = createNodes()
    }
    
    setup()
    window.addEventListener('resize', setup)
    
    const animate = () => {
      const state = stateRef.current
      const nodes = state.nodes
      if (state.clickBoost > 0) state.clickBoost = Math.max(0, state.clickBoost - 0.015)
      
      const bgGradient = ctx.createLinearGradient(0, 0, width, height)
      bgGradient.addColorStop(0, '#0a1628')
      bgGradient.addColorStop(0.5, '#0d1f3c')
      bgGradient.addColorStop(1, '#081430')
      ctx.fillStyle = bgGradient
      ctx.fillRect(0, 0, width, height)
      
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]
        node.x += node.vx
        node.y += node.vy
        if (node.x < 0) { node.x = 0; node.vx *= -1 }
        if (node.x > width) { node.x = width; node.vx *= -1 }
        if (node.y < 0) { node.y = 0; node.vy *= -1 }
        if (node.y > height) { node.y = height; node.vy *= -1 }
        node.radius = node.baseRadius * (1 + state.clickBoost * 0.5)
      }
      
      ctx.lineWidth = 1
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y
          const distance = Math.sqrt(dx * dx + dy * dy)
          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.6
            const boostedOpacity = Math.min(1, opacity + state.clickBoost * 0.3)
            const gradient = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y)
            gradient.addColorStop(0, `rgba(77, 130, 188, ${boostedOpacity})`)
            gradient.addColorStop(0.5, `rgba(132, 182, 244, ${boostedOpacity * 0.8})`)
            gradient.addColorStop(1, `rgba(77, 130, 188, ${boostedOpacity})`)
            ctx.strokeStyle = gradient
            ctx.beginPath()
            ctx.moveTo(nodes[i].x, nodes[i].y)
            ctx.lineTo(nodes[j].x, nodes[j].y)
            ctx.stroke()
          }
        }
      }
      
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]
        const glowRadius = node.radius * 4
        const glowGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowRadius)
        const glowIntensity = 0.15 + state.clickBoost * 0.2
        glowGradient.addColorStop(0, `rgba(132, 182, 244, ${glowIntensity})`)
        glowGradient.addColorStop(0.5, `rgba(77, 130, 188, ${glowIntensity * 0.3})`)
        glowGradient.addColorStop(1, 'rgba(77, 130, 188, 0)')
        ctx.fillStyle = glowGradient
        ctx.beginPath()
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2)
        ctx.fill()
        
        const nodeGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.radius)
        nodeGradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 + state.clickBoost * 0.1})`)
        nodeGradient.addColorStop(0.3, `rgba(196, 218, 250, ${0.8 + state.clickBoost * 0.1})`)
        nodeGradient.addColorStop(1, `rgba(77, 130, 188, 0.6)`)
        ctx.fillStyle = nodeGradient
        ctx.beginPath()
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
        ctx.fill()
      }
      
      if (cardRect) {
        const cardCenterX = cardRect.x + cardRect.width / 2
        const cardCenterY = cardRect.y + cardRect.height / 2
        const glowRadius = Math.max(cardRect.width, cardRect.height) * 0.8
        const cardGlow = ctx.createRadialGradient(cardCenterX, cardCenterY, 0, cardCenterX, cardCenterY, glowRadius)
        const intensity = 0.1 + state.clickBoost * 0.2
        cardGlow.addColorStop(0, `rgba(0, 81, 135, ${intensity})`)
        cardGlow.addColorStop(0.5, `rgba(77, 130, 188, ${intensity * 0.3})`)
        cardGlow.addColorStop(1, 'rgba(0, 81, 135, 0)')
        ctx.fillStyle = cardGlow
        ctx.fillRect(0, 0, width, height)
      }
      animationRef.current = requestAnimationFrame(animate)
    }
    
    stateRef.current.nodes = createNodes()
    animationRef.current = requestAnimationFrame(animate)
    
    return () => {
      window.removeEventListener('resize', setup)
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [cardRect])
  
  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />
})

// ============================================================================
// LOGIN SCREEN
// ============================================================================
function LoginScreen({ onLogin, isLoading, error }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)
  const [cardRect, setCardRect] = useState(null)
  const cardRef = useRef(null)
  const canvasRef = useRef(null)
  
  // Cargar credenciales guardadas
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail')
    const savedRemember = localStorage.getItem('rememberMe') === 'true'
    if (savedEmail && savedRemember) {
      setEmail(savedEmail)
      setRememberMe(true)
    }
  }, [])
  
  useEffect(() => {
    const updateCardRect = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect()
        setCardRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height })
      }
    }
    updateCardRect()
    window.addEventListener('resize', updateCardRect)
    const timer = setTimeout(updateCardRect, 100)
    return () => { window.removeEventListener('resize', updateCardRect); clearTimeout(timer) }
  }, [])
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoginError('')
    setIsSubmitting(true)
    if (canvasRef.current?.triggerPulse) canvasRef.current.triggerPulse()
    
    // Guardar o eliminar credenciales
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email)
      localStorage.setItem('rememberMe', 'true')
    } else {
      localStorage.removeItem('rememberedEmail')
      localStorage.removeItem('rememberMe')
    }
    
    try {
      const { data: authData, error: authError } = await supabaseAuth.signIn(email, password)
      if (authData && !authError) {
        const authUser = await supabaseAuth.getCurrentUser()
        if (authUser) {
          const admin = await findAdminByAuthId(authUser.id)
          if (admin) { onLogin(admin); return }
        }
      }
      setLoginError(authError || 'Email o contraseña incorrectos')
    } catch (err) {
      setLoginError('Error de conexión. Intenta de nuevo.')
    }
    setIsSubmitting(false)
  }
  
  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setForgotLoading(true)
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      })
      setForgotSent(true)
    } catch (err) {
      // Mostrar éxito de todas formas por seguridad
      setForgotSent(true)
    }
    setForgotLoading(false)
  }
  
  // Campos y botones con las medidas del sistema: radio 10, primario en azul de
  // marca con su sombra y secundario blanco con borde.
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '13px', background: C.bg, color: C.ink, outline: 'none', boxSizing: 'border-box' }
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }
  const btnPrimario = { border: 'none', borderRadius: 10, background: C.brand, color: '#fff', fontSize: '13px', fontWeight: 800, boxShadow: '0 3px 10px rgba(0,81,135,.28)' }
  const btnSecundario = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, color: C.page, fontSize: '13px', fontWeight: 800 }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1f2a37', padding: '16px', position: 'relative', overflow: 'hidden' }}>
      <AnimatedLoginBackground ref={canvasRef} cardRect={cardRect} />
      <div ref={cardRef} style={{ width: '100%', maxWidth: '360px', background: 'rgba(255, 255, 255, 0.98)', borderRadius: '12px', padding: '28px', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', position: 'relative', zIndex: 1, backdropFilter: 'blur(10px)' }}>
        {/* Sin logotipo: el menú lateral de la suite ya pinta la marca al lado y
            verla dos veces era el defecto más visible del conjunto. */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ color: C.ink, fontSize: '19px', fontWeight: 800, margin: '0 0 4px 0', letterSpacing: -0.2 }}>Customer Leads</h1>
          <p style={{ color: C.muted, fontSize: '12.5px', margin: 0 }}>Grupo Dimoauto - Monitorización</p>
        </div>

        {showForgotPassword ? (
          <div>
            <h2 style={{ fontSize: '15px', fontWeight: 800, color: C.ink, marginBottom: '6px' }}>🔑 Recuperar contraseña</h2>
            <p style={{ fontSize: '12.5px', color: C.muted, marginBottom: '14px' }}>Te enviaremos un enlace para restablecer tu contraseña.</p>
            {forgotSent ? (
              <div>
                <div style={{ background: `${C.verde}18`, border: `1px solid ${C.verde}44`, borderRadius: 10, padding: '12px', marginBottom: '14px', textAlign: 'center' }}>
                  <span style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}>✉️</span>
                  <span style={{ color: '#1f7a44', fontSize: '12px' }}>Si el email existe, recibirás un enlace de recuperación.</span>
                </div>
                <button onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail('') }} style={{ ...btnSecundario, width: '100%', padding: '10px', cursor: 'pointer' }}>Volver al login</button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="usuario@dimoauto.com" required style={inputStyle} />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={() => setShowForgotPassword(false)} style={{ ...btnSecundario, flex: 1, padding: '10px', cursor: 'pointer' }}>Cancelar</button>
                  <button type="submit" disabled={forgotLoading} style={{ ...btnPrimario, flex: 1, padding: '10px', cursor: forgotLoading ? 'wait' : 'pointer' }}>{forgotLoading ? 'Enviando...' : 'Enviar enlace'}</button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <>
            {error && (
              <div style={{ background: `${C.rojo}18`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '10px 12px', marginBottom: '14px' }}>
                <span style={{ color: C.rojo, fontSize: '12px' }}>⚠️ {error}</span>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="usuario@dimoauto.com" required style={inputStyle} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Contraseña</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required style={inputStyle} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '11px', color: C.muted }}>
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ width: '14px', height: '14px', accentColor: C.brand, cursor: 'pointer' }} />
                  Recordar credenciales
                </label>
                <button type="button" onClick={() => setShowForgotPassword(true)} style={{ background: 'none', border: 'none', color: C.brand, fontSize: '11px', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>¿Olvidaste tu contraseña?</button>
              </div>
              {loginError && (
                <div style={{ background: `${C.rojo}18`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '10px 12px', marginBottom: '14px', textAlign: 'center' }}>
                  <span style={{ color: C.rojo, fontSize: '12px' }}>❌ {loginError}</span>
                </div>
              )}
              <button type="submit" disabled={isSubmitting || isLoading} style={{ ...btnPrimario, width: '100%', padding: '11px', background: isSubmitting ? C.faint : C.brand, boxShadow: isSubmitting ? 'none' : btnPrimario.boxShadow, cursor: isSubmitting ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {isSubmitting ? (<><span style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>Verificando...</>) : (<>🔐 Iniciar Sesión</>)}
              </button>
            </form>
          </>
        )}
        <div style={{ marginTop: '16px', padding: '8px', background: `${C.verde}14`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <span style={{ fontSize: '14px' }}>🔒</span>
          <span style={{ fontSize: '10px', color: '#1f7a44' }}>Conexión segura con autenticación cifrada</span>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ============================================================================
// CHANGE PASSWORD MODAL
// ============================================================================
function ChangePasswordModal({ onClose, admin }) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  
  const handleChangePassword = async (e) => {
    e.preventDefault()
    setError('')
    if (newPassword.length < 6) { setError('La nueva contraseña debe tener al menos 6 caracteres'); return }
    if (newPassword !== confirmPassword) { setError('Las contraseñas no coinciden'); return }
    setIsLoading(true)
    try {
      const { error: verifyError } = await supabaseAuth.signIn(admin.email, currentPassword)
      if (verifyError) { setError('La contraseña actual es incorrecta'); setIsLoading(false); return }
      const token = localStorage.getItem('sb-access-token')
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword })
      })
      if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.message || 'Error al cambiar la contraseña') }
      setSuccess(true)
      setTimeout(() => onClose(), 2000)
    } catch (err) { setError(err.message || 'Error al cambiar la contraseña') }
    finally { setIsLoading(false) }
  }
  
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: `1px solid ${C.line}`, overflow: 'hidden' }}>
        <div style={{ background: C.brand, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '15px', fontWeight: 800 }}>🔐 Cambiar Contraseña</h3>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px' }}>✕</button>
        </div>
        <form onSubmit={handleChangePassword} style={{ padding: '24px' }}>
          {success ? (
            <div style={{ background: `${C.verde}18`, border: `1px solid ${C.verde}44`, borderRadius: 12, padding: '20px', textAlign: 'center' }}>
              <span style={{ fontSize: '40px', display: 'block', marginBottom: '10px' }}>✅</span>
              <p style={{ color: C.verde, fontWeight: 800, margin: 0 }}>Contraseña cambiada correctamente</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }}>Usuario</label>
                <input type="text" value={admin?.email || ''} disabled style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '13px', background: C.bg, color: C.muted, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }}>Contraseña actual *</label>
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '13px', color: C.ink, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }}>Nueva contraseña *</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '13px', color: C.ink, outline: 'none', boxSizing: 'border-box' }} />
                <span style={{ fontSize: '11px', color: C.muted }}>Mínimo 6 caracteres</span>
              </div>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' }}>Confirmar nueva contraseña *</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '13px', color: C.ink, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {error && <div style={{ background: `${C.rojo}18`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '10px 12px', marginBottom: '16px', color: C.rojo, fontSize: '12.5px' }}>❌ {error}</div>}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={onClose} className="fg-nav" style={{ flex: 1, padding: '11px', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, color: C.page, fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={isLoading} style={{ flex: 1, padding: '11px', background: C.brand, border: 'none', borderRadius: 10, color: 'white', fontSize: '13px', fontWeight: 800, boxShadow: '0 3px 10px rgba(0,81,135,.28)', cursor: isLoading ? 'wait' : 'pointer' }}>{isLoading ? 'Guardando...' : 'Cambiar Contraseña'}</button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  )
}

// ============================================================================
// COMPONENTES UI
// ============================================================================
// Icono de cada tarjeta de cifra, resuelto por el título para no tener que
// tocar las llamadas de abajo.
const ICONO_STAT = {
  'Leads Hoy': <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>,
  'Leads Semana': <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></>,
  'T. Resp.': <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  'Marcas': <><path d="M3 21h18" /><path d="M5 21V8l7-4 7 4v13" /></>,
  'Errores': <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>,
}

// La tarjeta de cifra de siempre, con las mismas props, pintada con la pieza del
// sistema. Algunos valores no son números ("12m", "6/8"): esos van por
// `displayValue`, que es la vía de la pieza para textos, y los numéricos por
// `value`, que además los cuenta al entrar.
function StatCard({ title, value, subtitle, color = C.brand }) {
  const numerico = value != null && value !== '' && !isNaN(value)
  return (
    <StatCardSistema
      label={title}
      value={numerico ? value : null}
      displayValue={numerico ? undefined : value}
      sub={subtitle}
      color={color}
      icon={<Ic d={ICONO_STAT[title] || <circle cx="12" cy="12" r="8" />} />}
    />
  )
}

function MarcaCard({ marca, onToggle }) {
  const config = MARCA_CONFIG[marca.nombre] || { bg: '#2b333d', text: '#fff' }
  return (
    // Tarjeta del sistema: borde fino, radio 12 y elevación con `fg-card`.
    <div className="fg-card" style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.line}` }}>
      <div style={{ background: config.bg, padding: '7px 8px', textAlign: 'center' }}>
        <h3 style={{ margin: 0, color: config.text, fontSize: 12.5, fontWeight: 800 }}>{marca.nombre}</h3>
      </div>
      <div style={{ padding: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <div style={{ textAlign: 'center', padding: '5px 3px', background: C.bg, borderRadius: 10 }}>
            <p style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: 0, lineHeight: 1.05 }}>{marca.leads_24h || 0}</p>
            <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.muted, margin: 0 }}>24h</p>
          </div>
          <div style={{ textAlign: 'center', padding: '5px 3px', background: C.bg, borderRadius: 10 }}>
            <p style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: 0, lineHeight: 1.05 }}>{marca.leads_7d || 0}</p>
            <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.muted, margin: 0 }}>7d</p>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${C.line}`, marginTop: 8, paddingTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 13, fontWeight: 800, margin: 0, color: marca.tiempo_respuesta_promedio && marca.tiempo_respuesta_promedio <= 30 ? C.verde : marca.tiempo_respuesta_promedio ? C.naranja : C.muted }}>
              {marca.tiempo_respuesta_promedio ? `${Math.round(marca.tiempo_respuesta_promedio)}m` : '-'}
            </p>
            <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.muted, margin: 0 }}>T.Resp</p>
          </div>
          <button onClick={() => onToggle(marca)} title={marca.sesion_valida ? 'Desactivar' : 'Activar'} style={{ width: 22, height: 22, borderRadius: 999, border: `1px solid ${marca.sesion_valida ? C.verde : C.line}`, cursor: 'pointer', background: marca.sesion_valida ? C.verde : '#fff', color: marca.sesion_valida ? '#fff' : C.faint, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
            {marca.sesion_valida ? '✓' : '○'}
          </button>
        </div>
      </div>
    </div>
  )
}

function LeadRow({ lead, onRetry }) {
  const config = MARCA_CONFIG[lead.marca_nombre] || { bg: '#2b333d', text: '#fff', logo: null }
  // Estos flags siguen alimentando el background del <tr> (filas de error en
  // rojo claro, pendientes/reintentando en amarillo claro). El helper antiguo
  // getEstadoBadge() ha sido sustituido por el chip inline de la columna
  // ASIGNACIÓN para alinear el diseño con EmailLeadsApp.
  const isError = lead.estado === 'error'
  const isPendiente = lead.estado === 'pendiente'
  const isReintentando = lead.estado === 'reintentando'

  return (
    <tr style={{ background: isError ? `${C.rojo}0d` : isPendiente || isReintentando ? `${C.naranja}0d` : '#fff' }}>
      {/* ORIGEN — chip de texto con el color de la marca (sin logo, igual */}
      {/* que en EmailLeadsApp donde la primera columna es un chip coloreado) */}
      <Td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, padding: '3px 10px', borderRadius: 999, background: config.bg, color: config.text }}>{lead.marca_nombre || '-'}</span>
      </Td>
      {/* CLIENTE — nombre + apellidos (apellidos viene en v_leads_recientes) */}
      <Td strong color={C.ink}>{[lead.nombre_cliente, lead.apellidos].filter(Boolean).join(' ') || '-'}</Td>
      {/* CONTACTO — telefono + email en dos lineas (mismo patron Email Leads) */}
      <Td>
        <div>{lead.telefono || '-'}</div>
        <div style={{ fontSize: 11, color: C.muted }}>{lead.email || '-'}</div>
      </Td>
      {/* VEHICULO — marca + (producto_interes + motor) en dos lineas.       */}
      {/* El portal Customer360 a veces parte el modelo entre producto_interes  */}
      {/* (ej: "E-3008") y motor (ej: "Allure Electrico 210 (157 kW)").         */}
      {/* Concatenamos para que la columna muestre el modelo completo.          */}
      <Td>
        <div style={{ fontWeight: 700, color: C.ink }}>{lead.marca_nombre || '-'}</div>
        <div style={{ fontSize: 11, color: C.muted, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={[lead.producto_interes, lead.motor].filter(Boolean).join(' ') || ''}>{[lead.producto_interes, lead.motor].filter(Boolean).join(' ') || '-'}</div>
      </Td>
      {/* TIPO — tipo_venta (VN/VO/B2B) leído del portal Customer360.        */}
      {/* Si la fila aún no tiene tipo_venta (lead pre-migración) caemos a    */}
      {/* tipo_cliente (B2B/B2C) para no dejar la celda vacía.                */}
      <Td>
        <Pill soft>{lead.tipo_venta || lead.tipo_cliente || '-'}</Pill>
      </Td>
      {/* ASIGNACION — chip estilo Email Leads (asignado/pendiente/error)    */}
      {/* preservando retry crítico: en error mantenemos botón ↻ y en        */}
      {/* reintentando un mini spinner — la app de Customer360 vive de eso.  */}
      <Td>{(() => {
        // Mismo texto de siempre; lo que cambia es la píldora, que ahora es la
        // del sistema (radio 999, fondo al 18 % del color y borde al 44 %).
        const map = {
          procesado:    { txt: 'asignado',  color: C.verde },
          pendiente:    { txt: 'pendiente', color: C.naranja },
          reintentando: { txt: 'retry',     color: C.brandDark },
          error:        { txt: 'error',     color: C.rojo },
        }
        const info = map[lead.estado] || map.pendiente
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Pill color={info.color} soft>{info.txt}</Pill>
            {lead.estado === 'error' && (
              <button onClick={() => onRetry(lead)} title={lead.error_mensaje || 'Reintentar'} style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999, border: `1px solid ${C.rojo}44`, background: '#fff', color: C.rojo, cursor: 'pointer' }}>↻</button>
            )}
            {lead.estado === 'reintentando' && (
              <span style={{ width: 9, height: 9, border: `1.5px solid ${C.brandDark}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block' }}></span>
            )}
          </span>
        )
      })()}</Td>
      {/* FECHA — formato absoluto dd/mm/aaaa, hh:mm (igual Email Leads) */}
      <Td color={C.muted}>{formatDateTime(lead.created_at)}</Td>
    </tr>
  )
}

function SystemStatus({ ultimaEjecucion }) {
  const isRecent = ultimaEjecucion && (Date.now() - new Date(ultimaEjecucion.ejecutado_en)) < 1000 * 60 * 35
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {/* Píldora del sistema: fondo al 18 % del color de estado y borde al 44 %. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, padding: '3px 10px', borderRadius: 999, color: isRecent ? C.verde : C.naranja, background: isRecent ? `${C.verde}18` : `${C.naranja}18`, border: `1px solid ${isRecent ? C.verde : C.naranja}44` }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: isRecent ? C.verde : C.naranja, animation: 'pulse 2s infinite' }}></span>
        {isRecent ? 'Activo' : 'Revisar'}
      </span>
      {ultimaEjecucion && <span style={{ fontSize: 11, color: C.muted }}>Sync: {formatTimeAgo(ultimaEjecucion.ejecutado_en)}</span>}
    </div>
  )
}

// ============================================================================
// BACKEND LOGS PANEL (Live desde Render.com)
// ============================================================================
function BackendLogsPanel() {
  const [logs, setLogs] = useState([])
  const [isConnected, setIsConnected] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [screenshots, setScreenshots] = useState([])
  const [backendInfo, setBackendInfo] = useState(null)
  // ── Corrida manual ───────────────────────────────────────────────────────
  // Lista de marcas (cargada desde GET /marcas), marca seleccionada y estado
  // de ejecución del POST /procesar-marca-manual.
  const [marcas, setMarcas] = useState([])
  const [marcaSel, setMarcaSel] = useState('')
  const [ejecutando, setEjecutando] = useState(false)
  const [ejecResult, setEjecResult] = useState(null) // { ok, msg }
  const [repetir, setRepetir] = useState(1) // nº de ejecuciones consecutivas
  const logsEndRef = useRef(null)

  const checkBackend = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/health`, { mode: 'cors' })
      if (res.ok) {
        const data = await res.json()
        setIsConnected(true)
        setBackendInfo(data)
      } else {
        setIsConnected(false)
        setBackendInfo(null)
      }
    } catch (err) {
      setIsConnected(false)
      setBackendInfo(null)
    }
  }

  const fetchLogs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/logs`, { mode: 'cors' })
      if (res.ok) {
        const data = await res.json()
        if (data.logs) setLogs(data.logs.slice(-30))
      }
    } catch (err) { /* ignore */ }
  }

  const fetchScreenshots = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/screenshots`, { mode: 'cors' })
      if (res.ok) {
        const data = await res.json()
        setScreenshots(data.screenshots || [])
      }
    } catch (err) { /* ignore */ }
  }

  const refreshAll = () => {
    checkBackend()
    fetchLogs()
    fetchScreenshots()
  }

  useEffect(() => {
    refreshAll()
    const interval = setInterval(refreshAll, 15000)
    return () => clearInterval(interval)
  }, [])

  // Cargar lista de marcas para el desplegable de corrida manual.
  // Si el endpoint /marcas no existe (backend antiguo) caemos a un fallback
  // con las marcas conocidas para que la UI no quede vacía.
  useEffect(() => {
    let cancelado = false
    const cargarMarcas = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/marcas`, { mode: 'cors' })
        if (res.ok) {
          const data = await res.json()
          if (!cancelado && Array.isArray(data.marcas) && data.marcas.length) {
            setMarcas(data.marcas)
            setMarcaSel(prev => prev || data.marcas[0].key)
            return
          }
        }
      } catch (_) { /* fallback abajo */ }
      if (!cancelado) {
        const fallback = [
          { key: 'peugeot',  nombre: 'Peugeot' },
          { key: 'citroen',  nombre: 'Citroën' },
          { key: 'opel',     nombre: 'Opel' },
          { key: 'fiat',     nombre: 'Fiat' },
          { key: 'jeep',     nombre: 'Jeep' },
          { key: 'fiat pro', nombre: 'Fiat Pro' },
          { key: 'abarth',   nombre: 'Abarth' },
        ]
        setMarcas(fallback)
        setMarcaSel(prev => prev || fallback[0].key)
      }
    }
    cargarMarcas()
    return () => { cancelado = true }
  }, [])

  // Lanza la corrida manual: el backend abrirá el portal Stellantis con las
  // credenciales de la marca seleccionada y procesará el primer lead pendiente.
  const ejecutarCorridaManual = async () => {
    if (!marcaSel || ejecutando) return
    setEjecutando(true)
    setEjecResult(null)
    setIsExpanded(true) // abrir el panel para que el usuario vea los logs

    // Bucle de ejecuciones consecutivas: corre `repetir` veces seguidas,
    // mostrando progreso (1/3), (2/3)... Se detiene en error o 429.
    for (let i = 0; i < repetir; i++) {
      const label = repetir > 1 ? ` (${i + 1}/${repetir})` : ''
      try {
        const res = await fetch(`${BACKEND_URL}/procesar-marca-manual`, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ marca: marcaSel }),
        })
        const data = await res.json().catch(() => ({}))
        if (res.ok && data.success) {
          setEjecResult({ ok: true, msg: `OK · ${data.leadsProcessed ?? 0} lead(s)${label}` })
          refreshAll()
          if (i < repetir - 1) {
            // Pausa breve entre ejecuciones para que el backend libere el mutex
            await new Promise(r => setTimeout(r, 1500))
          }
        } else if (res.status === 429) {
          setEjecResult({ ok: false, msg: `Backend ocupado${label}` })
          break
        } else {
          setEjecResult({ ok: false, msg: (data.error || `Error ${res.status}`) + label })
          break
        }
      } catch (e) {
        setEjecResult({ ok: false, msg: (e.message || 'Error de red') + label })
        break
      }
    }

    setEjecutando(false)
    refreshAll()
  }

  // Desatasca el mutex del backend cuando un run quedó colgado (Playwright
  // hung, browser crash silencioso). Llama a POST /reset-mutex.
  const resetMutex = async () => {
    // window.confirm en vez de confirm global — react-scripts/eslint
    // (no-restricted-globals) bloquea el build con `confirm` desnudo.
    if (!window.confirm('¿Liberar el mutex del backend? Solo si hay un run colgado.')) return
    try {
      const res = await fetch(`${BACKEND_URL}/reset-mutex`, { method: 'POST', mode: 'cors' })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        setEjecResult({ ok: true, msg: 'Mutex liberado' })
      } else {
        setEjecResult({ ok: false, msg: data.error || `Error ${res.status}` })
      }
    } catch (e) {
      setEjecResult({ ok: false, msg: e.message || 'Error de red' })
    } finally {
      refreshAll()
    }
  }

  useEffect(() => {
    if (logsEndRef.current && isExpanded) logsEndRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [logs, isExpanded])

  const getLogType = (msg) => {
    if (msg.includes('✅') || msg.includes('success') || msg.includes('OK')) return 'success'
    if (msg.includes('❌') || msg.includes('Error') || msg.includes('error')) return 'error'
    if (msg.includes('⚠️') || msg.includes('warning')) return 'warning'
    if (msg.includes('🔍') || msg.includes('debug')) return 'debug'
    return 'info'
  }
  const logColors = { info: '#9aa4af', success: '#2e9e5b', error: '#d23b34', warning: '#e08c1e', debug: '#7a5bd0' }

  return (
    // Consola del backend: fondo oscuro a propósito (son logs en crudo), pero
    // con los radios del sistema — 12 en la tarjeta, 10 en controles.
    <div style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      maxWidth: '48%',
      margin: '0 auto 8px',
      border: '1px solid #2b333d'
    }}>
      <div onClick={() => setIsExpanded(!isExpanded)} style={{ padding: '5px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: 'rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: isConnected ? '#2e9e5b' : '#d23b34', boxShadow: `0 0 4px ${isConnected ? '#2e9e5b' : '#d23b34'}`, animation: isConnected ? 'pulse 2s infinite' : 'none' }}></span>
          <span style={{ color: '#fff', fontSize: 12.5, fontWeight: 800 }}>🖥️ Backend</span>
          <span style={{ color: C.muted, fontSize: 11 }}>({isConnected ? 'On' : 'Off'})</span>
          {backendInfo && <span style={{ color: C.muted, fontSize: 11 }}>| {backendInfo.cronInterval}min</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          {/* ── Corrida manual: marca + ejecutar ─────────────────────────── */}
          <select
            value={marcaSel}
            onChange={(e) => setMarcaSel(e.target.value)}
            disabled={ejecutando || !isConnected}
            title="Marca para corrida manual"
            style={{
              background: '#1f2a37', color: '#e7e9ec', border: '1px solid #2b333d',
              borderRadius: 10, fontSize: 11, padding: '4px 8px', cursor: 'pointer',
              opacity: (ejecutando || !isConnected) ? 0.5 : 1,
            }}
          >
            {marcas.map(m => (
              <option key={m.key} value={m.key}>{m.nombre}</option>
            ))}
          </select>
          <button
            onClick={ejecutarCorridaManual}
            disabled={ejecutando || !isConnected || !marcaSel}
            title={`Procesar ${repetir > 1 ? repetir + ' leads consecutivos' : 'primer lead pendiente'} de ${marcas.find(m => m.key === marcaSel)?.nombre || marcaSel}`}
            style={{
              background: ejecutando ? '#2b333d' : repetir > 1 ? C.brand : C.verde,
              border: 'none', borderRadius: 10, padding: '5px 11px',
              color: '#fff', fontSize: 11, fontWeight: 800,
              cursor: (ejecutando || !isConnected) ? 'not-allowed' : 'pointer',
              opacity: (!isConnected) ? 0.5 : 1,
            }}
          >
            {ejecutando ? '⏳ Ejecutando…' : repetir > 1 ? `▶▶ Ejecutar ×${repetir}` : '▶ Ejecutar'}
          </button>
          <button
            onClick={resetMutex}
            disabled={!isConnected}
            title="Liberar mutex del backend si un run quedó colgado"
            style={{
              background: '#7c2d12',
              border: 'none', borderRadius: 10, padding: '5px 9px',
              color: '#fff', fontSize: 11, fontWeight: 800,
              cursor: !isConnected ? 'not-allowed' : 'pointer',
              opacity: !isConnected ? 0.5 : 1,
            }}
          >
            ↺
          </button>
          {ejecResult && (
            <span style={{
              fontSize: 11,
              color: ejecResult.ok ? '#6bbd8a' : '#dd6a64',
              maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} title={ejecResult.msg}>
              {ejecResult.ok ? '✅' : '⚠️'} {ejecResult.msg}
            </span>
          )}
          <button onClick={() => refreshAll()} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10, padding: '5px 9px', color: C.faint, fontSize: 11, cursor: 'pointer' }}>🔄</button>
          <span onClick={() => setIsExpanded(!isExpanded)} style={{ color: '#8a929c', fontSize: 14, cursor: 'pointer' }}>{isExpanded ? '▼' : '▶'}</span>
        </div>
      </div>
      {/* ── Fila stepper: nº ejecuciones consecutivas ────────────── */}
      <div style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6, borderTop: '1px solid #1f2a37', background: 'rgba(0,0,0,0.15)' }}>
        <span style={{ color: C.muted, fontSize: 11, fontWeight: 700, marginRight: 2 }}>Corridas:</span>
        <button
          onClick={() => setRepetir(r => Math.max(1, r - 1))}
          disabled={ejecutando || repetir <= 1}
          style={{
            background: '#1f2a37', border: '1px solid #2b333d', borderRadius: '10px 0 0 10px',
            color: C.faint, fontSize: 12, padding: '3px 9px', lineHeight: 1,
            cursor: (ejecutando || repetir <= 1) ? 'not-allowed' : 'pointer',
            opacity: (ejecutando || repetir <= 1) ? 0.4 : 1,
          }}>−</button>
        <span style={{
          background: '#1f2a37', border: '1px solid #2b333d',
          color: repetir > 1 ? '#e8ac52' : C.muted,
          fontSize: 12, fontWeight: 800, padding: '3px 9px', lineHeight: 1, minWidth: 24,
          textAlign: 'center', userSelect: 'none',
        }}>{repetir}</span>
        <button
          onClick={() => setRepetir(r => Math.min(10, r + 1))}
          disabled={ejecutando || repetir >= 10}
          style={{
            background: '#1f2a37', border: '1px solid #2b333d', borderRadius: '0 10px 10px 0',
            color: C.faint, fontSize: 12, padding: '3px 9px', lineHeight: 1,
            cursor: (ejecutando || repetir >= 10) ? 'not-allowed' : 'pointer',
            opacity: (ejecutando || repetir >= 10) ? 0.4 : 1,
          }}>+</button>
        {repetir > 1 && <span style={{ color: C.muted, fontSize: 11 }}>corridas seguidas</span>}
      </div>
      {isExpanded && (
        <div style={{ maxHeight: 120, overflowY: 'auto', padding: 6, background: 'rgba(0,0,0,0.2)' }}>
          {logs.length === 0 ? (
            <div style={{ color: C.muted, textAlign: 'center', padding: 30, fontSize: 12.5, fontWeight: 700 }}>Sin logs recientes</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {logs.map((log, i) => {
                const type = getLogType(log.message)
                const time = log.timestamp?.substring(11, 19) || '??'
                const msg = (log.message || '').replace(/^\[\d{2}:\d{2}:\d{2}\]\s*/, '')
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 10, borderLeft: `2px solid ${logColors[type]}`, alignItems: 'center', fontSize: 10 }}>
                    <span style={{ color: '#ffffff', fontSize: 11, minWidth: 60 }}>{time}</span>
                    <span style={{ color: '#e7e9ec', fontSize: 13, flex: 1, wordBreak: 'break-word' }}>{msg}</span>
                  </div>
                )
              })}
            </div>
          )}
          <div ref={logsEndRef} />
        </div>
      )}
      {isExpanded && screenshots.length > 0 && (
        <div style={{ borderTop: '1px solid #2b333d', padding: '4px 8px', background: '#1f2a37' }}>
          <div style={{ color: C.faint, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, marginBottom: 4 }}>📸 Screenshots ({screenshots.length}):</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {screenshots.slice(-8).map(s => (
              <a key={s} href={`${BACKEND_URL}/screenshot/${s}`} target="_blank" rel="noreferrer" style={{ fontSize: 10, fontWeight: 700, color: '#7cc4e8', textDecoration: 'none', background: '#2b333d', padding: '2px 9px', borderRadius: 999 }}>{s.replace('debug-', '').replace('.png', '').substring(0, 12)}</a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// LIVE AUTOMATION VIEW - Vista en vivo del proceso de automatización
// ============================================================================
function LiveAutomationView() {
  const [imgUrl, setImgUrl] = useState('')
  const [estado, setEstado] = useState(null)
  const [logs, setLogs] = useState([])
  const [isConnected, setIsConnected] = useState(false)
  const [expanded, setExpanded] = useState(true)

  // Actualizar screenshot cada 1.5 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      setImgUrl(`${BACKEND_URL}/live-screenshot?t=${Date.now()}`)
    }, 1500)
    return () => clearInterval(interval)
  }, [])

  // Obtener estado cada 3 segundos
  useEffect(() => {
    const fetchEstado = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/estado`, { mode: 'cors' })
        if (res.ok) {
          const data = await res.json()
          setEstado(data)
          setIsConnected(true)
        } else {
          setIsConnected(false)
        }
      } catch (e) {
        setIsConnected(false)
      }
    }
    fetchEstado()
    const interval = setInterval(fetchEstado, 3000)
    return () => clearInterval(interval)
  }, [])

  // Obtener logs cada 5 segundos
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/logs`, { mode: 'cors' })
        if (res.ok) {
          const data = await res.json()
          setLogs(data.logs || [])
        }
      } catch (e) {}
    }
    fetchLogs()
    const interval = setInterval(fetchLogs, 5000)
    return () => clearInterval(interval)
  }, [])

  const getStatusColor = () => {
    if (!isConnected) return '#8a929c'
    if (estado?.activo) return '#2e9e5b'
    if (estado?.error) return '#d23b34'
    return '#1a9cd4'
  }

  const getStatusText = () => {
    if (!isConnected) return 'Desconectado'
    if (estado?.activo) return `Procesando: ${estado.paso || '...'}`
    if (estado?.error) return `Error: ${estado.error}`
    return 'En espera'
  }

  // Si no está procesando y no hay errores, mostrar versión compacta
  if (!estado?.activo && !estado?.error && isConnected) {
    return (
      <div style={{
        background: '#2b333d',
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid #2b333d',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
        maxWidth: '72%',
        margin: '0 auto 8px'
      }}>
        <div
          style={{
            background: '#1f2a37',
            padding: '5px 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: getStatusColor()
            }} />
            <span style={{ color: '#f4f5f7', fontWeight: 800, fontSize: 12.5 }}>
              🤖 Automatización C360
            </span>
            <span style={{ color: C.faint, fontSize: 11 }}>
              (En espera)
            </span>
          </div>
          <span style={{ color: '#8a929c', fontSize: 14 }}>{expanded ? '▼' : '▶'}</span>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      background: '#2b333d',
      borderRadius: 12,
      overflow: 'hidden',
      border: estado?.activo ? `2px solid ${C.verde}` : '1px solid #2b333d',
      maxWidth: '72%',
      margin: '0 auto 8px',
      boxShadow: estado?.activo ? '0 0 10px rgba(16, 185, 129, 0.15)' : '0 2px 10px rgba(0,0,0,0.2)'
    }}>
      {/* Header */}
      <div
        style={{
          background: '#1f2a37',
          padding: '5px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderBottom: expanded ? '1px solid #2b333d' : 'none'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: getStatusColor(),
            animation: estado?.activo ? 'pulse 1.5s infinite' : 'none'
          }} />
          <span style={{ color: '#f4f5f7', fontWeight: 800, fontSize: 12.5 }}>
            🤖 Automatización C360
          </span>
          {estado?.marca && (
            <span style={{
              background: '#1a9cd4',
              color: '#fff',
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 0.4
            }}>
              {estado.marca}
            </span>
          )}
          {estado?.activo && (
            <span style={{
              background: C.verde,
              color: '#fff',
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 0.4,
              animation: 'pulse 1s infinite'
            }}>
              🔴 EN VIVO
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: C.faint, fontSize: 11 }}>
            {getStatusText()}
          </span>
          <span style={{ color: '#8a929c', fontSize: 14 }}>{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div style={{ padding: 16 }}>
          {/* Screenshot en vivo */}
          <div style={{
            background: '#1f2a37',
            borderRadius: 12,
            padding: 8,
            marginBottom: 16
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8
            }}>
              <span style={{ color: '#9aa4af', fontSize: 11 }}>
                📸 Vista en vivo del navegador
              </span>
              {estado?.activo && (
                <span style={{
                  color: '#2e9e5b',
                  fontSize: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  <span style={{
                    width: 6,
                    height: 6,
                    background: '#2e9e5b',
                    borderRadius: '50%',
                    animation: 'pulse 1s infinite'
                  }} />
                  TRANSMITIENDO
                </span>
              )}
            </div>
            
            {imgUrl ? (
              <img 
                src={imgUrl}
                alt="Live screenshot"
                style={{
                  width: '100%',
                  borderRadius: 10,
                  border: estado?.activo ? `2px solid ${C.verde}` : '1px solid #2b333d'
                }}
                onError={(e) => { e.target.style.opacity = '0.3' }}
                onLoad={(e) => { e.target.style.opacity = '1' }}
              />
            ) : (
              <div style={{
                height: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#8a929c',
                fontSize: 13
              }}>
                Sin imagen disponible - esperando proceso
              </div>
            )}
          </div>

          {/* Estado actual */}
          {estado && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
              marginBottom: 16
            }}>
              <div style={{
                background: '#1f2a37',
                padding: 12,
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ color: '#9aa4af', fontSize: 10, marginBottom: 4 }}>Estado</div>
                <div style={{ 
                  color: estado.activo ? '#2e9e5b' : '#8a929c',
                  fontSize: 12,
                  fontWeight: 600
                }}>
                  {estado.activo ? '🟢 Activo' : '⚪ Inactivo'}
                </div>
              </div>
              <div style={{
                background: '#1f2a37',
                padding: 12,
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ color: '#9aa4af', fontSize: 10, marginBottom: 4 }}>Paso actual</div>
                <div style={{ color: '#f4f5f7', fontSize: 12, fontWeight: 600 }}>
                  {estado.paso || '-'}
                </div>
              </div>
              <div style={{
                background: '#1f2a37',
                padding: 12,
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ color: '#9aa4af', fontSize: 10, marginBottom: 4 }}>Último lead</div>
                <div style={{ color: '#1a9cd4', fontSize: 12, fontWeight: 600 }}>
                  {estado.ultimoLead ? `#${estado.ultimoLead}` : '-'}
                </div>
              </div>
            </div>
          )}

          {/* Mini logs */}
          <div style={{
            background: '#1f2a37',
            borderRadius: 12,
            padding: 10,
            maxHeight: 120,
            overflow: 'auto'
          }}>
            <div style={{ color: '#9aa4af', fontSize: 11, marginBottom: 6 }}>
              📋 Últimas acciones
            </div>
            {logs.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {logs.slice(-8).reverse().map((log, i) => {
                  const color = log.message.includes('✅') ? '#2e9e5b' :
                                log.message.includes('❌') ? '#d23b34' :
                                log.message.includes('⚠️') ? '#e08c1e' : '#e7e9ec'
                  const time = log.timestamp?.substring(11, 19) || ''
                  const msg = (log.message || '').replace(/^\[\d{2}:\d{2}:\d{2}\]\s*/, '')
                  return (
                    <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 10, borderLeft: `2px solid ${color}`, alignItems: 'center', fontSize: 10 }}>
                      <span style={{ color: '#ffffff', fontSize: 11, minWidth: 60 }}>{time}</span>
                      <span style={{ color: '#e7e9ec', fontSize: 13, flex: 1, wordBreak: 'break-word' }}>{msg}</span>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div style={{ color: '#8a929c', fontSize: 11, textAlign: 'center', padding: 10 }}>
                Sin actividad reciente
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// HEADER
// ============================================================================
function Header({ admin, onLogout, onOpenChangePassword }) {
  return (
    // Cabecera con la forma de la suite: sin logotipo (lo pone el menú lateral),
    // título de 19 px y una línea que explica qué es esta pantalla.
    <header style={{ background: '#ffffff', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderBottom: `1px solid ${C.line}` }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 19, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: -0.2 }}>Customer Leads</h1>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, padding: '3px 10px', borderRadius: 999, color: C.verde, background: `${C.verde}18`, border: `1px solid ${C.verde}44` }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.verde, animation: 'pulse 2s infinite' }}></span>
            Supabase
          </span>
        </div>
        <p style={{ fontSize: 12.5, color: C.muted, margin: '3px 0 0' }}>Leads del portal Customer360 de Stellantis: marca a marca, con el robot que los recoge a la vista.</p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: C.page }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: C.faint }}>Grupo Dimoauto</span>
        {admin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div onClick={onOpenChangePassword} className="fg-nav" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#fff', borderRadius: 10, fontSize: '12.5px', fontWeight: 700, border: `1px solid ${C.line}`, cursor: 'pointer' }} title="Clic para cambiar contraseña">
              <span>👤</span><span>{admin.nombre}</span><span style={{ fontSize: '10px', opacity: 0.6 }}>🔐</span>
            </div>
            <button onClick={onLogout} className="fg-nav" style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: '6px 12px', color: C.rojo, cursor: 'pointer', fontSize: '12.5px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '5px' }} title="Cerrar sesión">🚪 Salir</button>
          </div>
        )}
      </div>
    </header>
  )
}

// ============================================================================
// APP PRINCIPAL
// ============================================================================
export default function CustomerLeadsApp() {
  const [admin, setAdmin] = useState(null)

  // ── Sesión compartida de la suite (cookie de *.dimoauto.com) ──────────────
  // Si ya se entró en el Centro de Aplicaciones, aquí no se vuelve a pedir la
  // contraseña. Si no hay sesión compartida, o el correo no es administrador
  // activo, se enseña el login de siempre: esto solo añade un atajo.
  const [sesionProbada, setSesionProbada] = useState(false);
  useEffect(() => {
    let vivo = true;
    entrarConSesionCompartida(SUPABASE_URL, SUPABASE_ANON_KEY)
      .then((a) => { if (vivo && a) setAdmin(a); })
      .finally(() => { if (vivo) setSesionProbada(true); });
    return () => { vivo = false; };
  }, []);
  const [authLoading, setAuthLoading] = useState(true)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [marcas, setMarcas] = useState([])
  const [leads, setLeads] = useState([])
  const [ultimaEjecucion, setUltimaEjecucion] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [filtroMarca, setFiltroMarca] = useState('todas')
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected') // 'connected', 'disconnected', 'error'
  
  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authUser = await supabaseAuth.getCurrentUser()
        if (authUser) {
          const adminData = await findAdminByAuthId(authUser.id)
          if (adminData) {
            setAdmin(adminData)
          } else {
            // User authenticated but not in administradores table - clear session
            await supabaseAuth.signOut()
          }
        }
      } catch (err) {
        console.error('Error checking auth:', err)
        // Clear any invalid session
        await supabaseAuth.signOut()
      }
      setAuthLoading(false)
    }
    checkAuth()
  }, [])
  
  // Load data when authenticated + Realtime subscriptions
  useEffect(() => {
    if (!admin) return
    setLoading(true)
    if (DEMO_MODE) {
      setMarcas(DEMO_MARCAS)
      setLeads(DEMO_LEADS)
      setUltimaEjecucion(DEMO_ULTIMA_EJECUCION)
      setLoading(false)
    } else {
      // Timeout de seguridad para nunca quedarse en loading
      const loadingTimeout = setTimeout(() => {
        setLoading(false)
        console.warn('Timeout de carga alcanzado')
      }, 10000)
      
      loadData().finally(() => clearTimeout(loadingTimeout))
      
      // Configurar Realtime con WebSocket
      let ws = null
      let heartbeatInterval = null
      try {
        const wsUrl = `${CUSTOMER_SUPABASE_URL.replace('https://', 'wss://')}/realtime/v1/websocket?apikey=${CUSTOMER_SUPABASE_ANON_KEY}&vsn=1.0.0`
        ws = new WebSocket(wsUrl)
        
        ws.onopen = () => {
          console.log('🟢 Realtime conectado')
          setRealtimeStatus('connected')
          // Join leads channel
          ws.send(JSON.stringify({ topic: 'realtime:public:leads', event: 'phx_join', payload: {}, ref: '1' }))
          // Join marcas channel
          ws.send(JSON.stringify({ topic: 'realtime:public:marcas', event: 'phx_join', payload: {}, ref: '2' }))
          // Heartbeat cada 30s para mantener conexión
          heartbeatInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ topic: 'phoenix', event: 'heartbeat', payload: {}, ref: 'hb' }))
            }
          }, 30000)
        }
        
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data)
            // Detectar INSERT, UPDATE, DELETE
            if (['INSERT', 'UPDATE', 'DELETE'].includes(msg.event)) {
              console.log('📥 Cambio en tiempo real:', msg.event)
              loadData()
            }
          } catch (e) { /* ignore */ }
        }
        
        ws.onerror = () => { console.warn('⚠️ Realtime error, usando polling'); setRealtimeStatus('error') }
        ws.onclose = () => { console.log('🔴 Realtime desconectado'); setRealtimeStatus('disconnected') }
      } catch (err) {
        console.warn('No se pudo conectar a Realtime')
        setRealtimeStatus('error')
      }
      // Polling de respaldo cada 10 segundos
      const interval = setInterval(loadData, 10000)
      return () => {
        clearInterval(interval)
        if (heartbeatInterval) clearInterval(heartbeatInterval)
        if (ws) ws.close()
      }
    }
  }, [admin])
  
  async function loadData() {
    try {
      setError(null)
      // Use customerSupabase for data (different from login Supabase)
      const marcasRes = await fetch(`${CUSTOMER_SUPABASE_URL}/rest/v1/v_resumen_marcas?select=*&order=nombre`, {
        headers: { 'apikey': CUSTOMER_SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CUSTOMER_SUPABASE_ANON_KEY}` }
      })
      const marcasData = await marcasRes.json()
      
      const leadsRes = await fetch(`${CUSTOMER_SUPABASE_URL}/rest/v1/v_leads_recientes?select=*&limit=50`, {
        headers: { 'apikey': CUSTOMER_SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CUSTOMER_SUPABASE_ANON_KEY}` }
      })
      const leadsData = await leadsRes.json()
      
      const ejecRes = await fetch(`${CUSTOMER_SUPABASE_URL}/rest/v1/ejecuciones_log?select=*&order=ejecutado_en.desc&limit=1`, {
        headers: { 'apikey': CUSTOMER_SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CUSTOMER_SUPABASE_ANON_KEY}` }
      })
      const ejecData = await ejecRes.json()
      
      // Asegurar que Fiat Pro siempre esté presente
      let marcasList = Array.isArray(marcasData) ? marcasData : []
      const hasFiatPro = marcasList.some(m => m.nombre === 'Fiat Pro')
      if (!hasFiatPro) {
        marcasList = [...marcasList, { id: 'fiat-pro', nombre: 'Fiat Pro', codigo: 'FIAPRO', activa: true, sesion_valida: true, leads_24h: 0, leads_7d: 0, tiempo_respuesta_promedio: null }]
      }
      
      // Ordenar: Peugeot, Citroën, Opel, Fiat, Fiat Pro, Jeep, Abarth, Leapmotor
      const ordenMarcas = ['Peugeot', 'Citroën', 'Opel', 'Fiat', 'Fiat Pro', 'Jeep', 'Abarth', 'Leapmotor']
      marcasList.sort((a, b) => {
        const idxA = ordenMarcas.indexOf(a.nombre)
        const idxB = ordenMarcas.indexOf(b.nombre)
        return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB)
      })
      
      // Actualizar marcas preservando sesion_valida local o usando true por defecto
      setMarcas(prev => {
        if (prev.length === 0) {
          // Primera carga: todas activas por defecto
          return marcasList.map(m => ({ ...m, sesion_valida: true }))
        }
        // Cargas siguientes: preservar estado local de sesion_valida, actualizar solo stats
        return marcasList.map(m => {
          const existing = prev.find(p => p.id === m.id || p.nombre === m.nombre)
          return { ...m, sesion_valida: existing ? existing.sesion_valida : true }
        })
      })
      
      setLeads(Array.isArray(leadsData) ? leadsData : [])
      setUltimaEjecucion(ejecData?.[0] || null)
    } catch (err) {
      console.error('Error cargando datos:', err)
      setError(err.message)
      setMarcas(DEMO_MARCAS)
      setLeads(DEMO_LEADS)
      setUltimaEjecucion(DEMO_ULTIMA_EJECUCION)
    } finally {
      setLoading(false)
    }
  }
  
  const handleLogin = (adminData) => { setAdmin(adminData) }
  const handleLogout = async () => { borrarSesion(); await supabaseAuth.signOut(); setAdmin(null) }
  
  async function handleToggleMarca(marca) {
    // Actualizar estado local inmediatamente
    const nuevoEstado = !marca.sesion_valida
    setMarcas(prev => prev.map(m => m.id === marca.id ? { ...m, sesion_valida: nuevoEstado } : m))
    
    // Si no es modo demo, actualizar en Supabase
    if (!DEMO_MODE && marca.id !== 'fiat-pro') {
      try {
        await fetch(`${CUSTOMER_SUPABASE_URL}/rest/v1/marcas?id=eq.${marca.id}`, {
          method: 'PATCH',
          headers: {
            'apikey': CUSTOMER_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${CUSTOMER_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify({ sesion_valida: nuevoEstado })
        })
      } catch (err) {
        console.error('Error actualizando marca:', err)
        // Revertir cambio local si falla
        setMarcas(prev => prev.map(m => m.id === marca.id ? { ...m, sesion_valida: !nuevoEstado } : m))
      }
    }
  }
  
  async function handleRetryLead(lead) {
    // Actualizar estado local inmediatamente
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, estado: 'reintentando' } : l))
    
    if (DEMO_MODE) {
      // Simular procesamiento en modo demo
      setTimeout(() => {
        setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, estado: 'procesado', tiempo_respuesta_mins: 15 } : l))
      }, 1500)
    } else {
      // Modo real: actualizar en Supabase
      try {
        // Marcar como pendiente de reintento en la tabla leads
        await fetch(`${CUSTOMER_SUPABASE_URL}/rest/v1/leads?id=eq.${lead.id}`, {
          method: 'PATCH',
          headers: {
            'apikey': CUSTOMER_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${CUSTOMER_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify({ estado: 'pendiente', error_mensaje: null })
        })
        // Actualizar UI
        setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, estado: 'pendiente', error_mensaje: null } : l))
      } catch (err) {
        console.error('Error reintentando lead:', err)
        // Revertir a estado de error
        setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, estado: 'error' } : l))
      }
    }
  }
  
  let leadsFiltrados = leads
  if (filtroMarca !== 'todas') leadsFiltrados = leadsFiltrados.filter(l => l.marca_nombre === filtroMarca)
  if (filtroEstado !== 'todos') leadsFiltrados = leadsFiltrados.filter(l => l.estado === filtroEstado)
  
  const totalLeads24h = marcas.reduce((sum, m) => sum + (m.leads_24h || 0), 0)
  const totalLeads7d = marcas.reduce((sum, m) => sum + (m.leads_7d || 0), 0)
  const tiempoPromedio = marcas.filter(m => m.tiempo_respuesta_promedio).length > 0 ? marcas.filter(m => m.tiempo_respuesta_promedio).reduce((sum, m, _, arr) => sum + m.tiempo_respuesta_promedio / arr.length, 0) : 0
  const marcasActivas = marcas.filter(m => m.sesion_valida).length
  const leadsConError = leads.filter(l => l.estado === 'error').length
  const leadsPendientes = leads.filter(l => l.estado === 'pendiente').length
  
  // Auth loading — el punto que respira de la suite en vez del anillo giratorio.
  if (authLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: C.ink, color: '#c4dafa', fontSize: 13.5, fontWeight: 600 }}>
        <span className="fg-breath" style={{ width: 9, height: 9, borderRadius: '50%', background: '#1a9cd4' }} />
        Verificando sesión...
      </div>
    )
  }

  // Not logged in - show login screen
  if (!admin && !sesionProbada) return <Entrando />;
  if (!admin) {
    return <LoginScreen onLogin={handleLogin} isLoading={false} error={null} />
  }

  // Data loading (only after authenticated) — esqueleto de la pantalla que va a
  // aparecer (cinco cifras, los dos paneles, las marcas y la tabla).
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, padding: '12px 12px 18px', maxWidth: 1600, margin: '0 auto' }}>
        <div className="fg-shimmer" style={{ height: 46, borderRadius: 12, marginBottom: 12 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 10 }}>
          {[0, 1, 2, 3, 4].map(i => <div key={i} className="fg-shimmer" style={{ height: 74, borderRadius: 12 }} />)}
        </div>
        <div className="fg-shimmer" style={{ height: 40, borderRadius: 12, maxWidth: '72%', margin: '0 auto 8px' }} />
        <div className="fg-shimmer" style={{ height: 120, borderRadius: 12, maxWidth: '48%', margin: '0 auto 10px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6, marginBottom: 10 }}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="fg-shimmer" style={{ height: 110, borderRadius: 12 }} />)}
        </div>
        <div className="fg-shimmer" style={{ height: 300, borderRadius: 12 }} />
      </div>
    )
  }
  
  return (
    <div className="fg-fade" style={{ minHeight: '100vh', background: C.bg }}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } } @keyframes spin { to { transform: rotate(360deg); } } table { width: 100%; border-collapse: collapse; } tbody tr:hover { background: #f7f8fa !important; } select:focus, button:focus { outline: none; }`}</style>

      <Header admin={admin} onLogout={handleLogout} onOpenChangePassword={() => setShowChangePassword(true)} />

      <div style={{ padding: '12px 12px 18px', maxWidth: 1600, margin: '0 auto' }}>
        {error && <div style={{ background: `${C.rojo}18`, border: `1px solid ${C.rojo}44`, color: C.rojo, padding: '8px 12px', borderRadius: 10, marginBottom: 10, fontSize: 12.5, fontWeight: 700 }}>⚠️ Error: {error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SystemStatus ultimaEjecucion={ultimaEjecucion} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, padding: '3px 10px', borderRadius: 999, background: realtimeStatus === 'connected' ? `${C.verde}18` : realtimeStatus === 'error' ? `${C.rojo}18` : `${C.gris}18`, border: `1px solid ${realtimeStatus === 'connected' ? `${C.verde}44` : realtimeStatus === 'error' ? `${C.rojo}44` : C.line}`, color: realtimeStatus === 'connected' ? C.verde : realtimeStatus === 'error' ? C.rojo : C.muted }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: realtimeStatus === 'connected' ? C.verde : realtimeStatus === 'error' ? C.rojo : C.faint, animation: realtimeStatus === 'connected' ? 'pulse 2s infinite' : 'none' }}></span>
              {realtimeStatus === 'connected' ? '🔴 LIVE' : realtimeStatus === 'error' ? 'Polling' : '...'}
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 10 }}>
          <StatCard title="Leads Hoy" value={totalLeads24h} subtitle="Recibidos hoy" color="#005187" />
          <StatCard title="Leads Semana" value={totalLeads7d} subtitle="Últimos 7 días" color="#005187" />
          <StatCard title="T. Resp." value={`${Math.round(tiempoPromedio)}m`} subtitle="Tiempo medio" color={tiempoPromedio <= 30 ? '#2e9e5b' : '#e08c1e'} />
          <StatCard title="Marcas" value={`${marcasActivas}/${marcas.length}`} subtitle="Activas" color={marcasActivas === marcas.length ? '#2e9e5b' : '#e08c1e'} />
          <StatCard title="Errores" value={leadsConError} subtitle="Por reintentar" color={leadsConError > 0 ? '#d23b34' : '#2e9e5b'} />
        </div>
        
        <LiveAutomationView />
        <BackendLogsPanel />
        
        <div className="fg-card" style={{ background: '#fff', borderRadius: 12, padding: 12, marginBottom: 10, border: `1px solid ${C.line}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, margin: '2px 0 10px' }}>
            <h2 style={{ fontSize: 14, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: 0.2 }}>Estado por Marca</h2>
            <span style={{ fontSize: 11, color: C.muted }}>Clic ✓/○ para activar/desactivar</span>
          </div>
          {marcas.length === 0 ? (
            // Sin marcas cargadas la rejilla se quedaba en blanco.
            <EmptyState icon="🏁" title="Todavía no hay marcas cargadas"
              hint="Las marcas del portal Customer360 aparecerán aquí en cuanto el backend haga su primera sincronización." />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 8 }}>
              {marcas.map(marca => <MarcaCard key={marca.id} marca={marca} onToggle={handleToggleMarca} />)}
            </div>
          )}
        </div>
        
        <div className="fg-card" style={{ background: '#fff', borderRadius: 12, padding: 12, border: `1px solid ${C.line}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, margin: '2px 0 10px' }}>
            <h2 style={{ fontSize: 14, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: 0.2 }}>Leads Recientes</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ background: '#fff', border: `1px solid ${C.line}`, color: C.page, padding: '6px 10px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                <option value="todos">Todos</option>
                <option value="procesado">✓ Procesados</option>
                <option value="pendiente">⏳ Pendientes</option>
                <option value="error">✗ Error</option>
              </select>
              <select value={filtroMarca} onChange={e => setFiltroMarca(e.target.value)} style={{ background: '#fff', border: `1px solid ${C.line}`, color: C.page, padding: '6px 10px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                <option value="todas">Todas marcas</option>
                {marcas.map(m => <option key={m.id} value={m.nombre}>{m.nombre}</option>)}
              </select>
            </div>
          </div>
          {leadsFiltrados.length === 0 ? (
            // Antes se veía la cabecera de la tabla y debajo una línea de texto.
            <EmptyState icon="📭" title="No hay leads para mostrar"
              hint="Prueba a quitar los filtros de estado o de marca: puede que sí haya leads fuera de la selección actual." />
          ) : (
            <TableWrap minWidth={880}>
              <thead>
                <tr>
                  {['Origen', 'Cliente', 'Contacto', 'Vehículo', 'Tipo', 'Asignación', 'Fecha'].map(h => <Th key={h}>{h}</Th>)}
                </tr>
              </thead>
              <tbody>{leadsFiltrados.map(lead => <LeadRow key={lead.id} lead={lead} onRetry={handleRetryLead} />)}</tbody>
            </TableWrap>
          )}
        </div>

        <footer style={{ marginTop: 12, paddingTop: 8, textAlign: 'center', color: C.faint, fontSize: 11 }}>
          <p style={{ margin: 0 }}>Customer 360 Report | Grupo Dimoauto</p>
          {DEMO_MODE && <p style={{ marginTop: 5, padding: '3px 10px', background: `${C.naranja}18`, border: `1px solid ${C.naranja}44`, borderRadius: 999, color: C.naranja, display: 'inline-block', fontSize: 10.5, fontWeight: 800 }}>⚠️ MODO DEMO</p>}
        </footer>
      </div>
      
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} admin={admin} />}
    </div>
  )
}
